import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiCheckCircle } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { STAGE_FIELDS, STAGE_NAMES } from './StageSubmissionForm';
import { updateSubmission } from '../../services/productDevelopmentService';

const SubmissionEditorModal = ({ submission, isOpen, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  const fields = useMemo(() => {
    if (!submission?.stage) return [];
    return STAGE_FIELDS[submission.stage] || [];
  }, [submission?.stage]);

  useEffect(() => {
    if (!submission) {
      setFormData({});
      return;
    }

    const nextValues = {};
    fields.forEach(field => {
      nextValues[field.id] = submission[field.id] || '';
    });
    setFormData(nextValues);
  }, [submission, fields]);

  if (!isOpen || !submission) {
    return null;
  }

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!submission?.id) return;

    setLoading(true);
    try {
      const payload = {
        stage: submission.stage,
        stageName: STAGE_NAMES[submission.stage] || submission.stageName,
        ...formData,
        attachments: submission.attachments || [],
        userId: submission.userId,
        userEmail: submission.userEmail,
      };

      await updateSubmission(submission.id, payload);
      toast.success('Submission updated successfully');
      onUpdated?.();
      onClose();
    } catch (error) {
      toast.error('Failed to update submission');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">
              Edit {STAGE_NAMES[submission.stage] || submission.stageName} Submission
            </h2>
            <p className="text-sm text-gray-600">Update the submission details for this client</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {fields.length === 0 ? (
              <p className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                No editable fields defined for this stage.
              </p>
            ) : (
              fields.map(field => (
                <div key={field.id}>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      name={field.id}
                      value={formData[field.id] || ''}
                      onChange={handleChange}
                      required={field.required}
                      rows={4}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder={`Update ${field.label.toLowerCase()}...`}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      name={field.id}
                      value={formData[field.id] || ''}
                      onChange={handleChange}
                      required={field.required}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder={`Update ${field.label.toLowerCase()}...`}
                    />
                  )}
                </div>
              ))
            )}

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
              Attachments remain unchanged. Use the delete action from the submissions table if you
              need to re-upload files.
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <HiCheckCircle className="h-5 w-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default SubmissionEditorModal;
